import React, {
  createContext,
  useContext,
  useEffect,
  useState,
  useCallback,
} from "react";
import { UserManager, WebStorageStateStore, User } from "oidc-client-ts";
import { fetchAuthConfig } from "./authConfig";
import { apiFetch, primeCsrf } from "../../shared/utils/http";
import { finalizeSessionOnClient } from "../session/utils/sessionFinalize";
import { logger } from "../../shared/utils/logger";
import { jwtDecode } from "jwt-decode";
import { Provider } from "../session/utils/userSessionStorage";

// ----------------- Provider memory -----------------
const LAST_PROVIDER_KEY = "loggedMethod";

const getLastProvider = (): Provider => {
  const p = (localStorage.getItem(LAST_PROVIDER_KEY) || "MYGOV") as Provider;
  return p === "EIDAS" ? "EIDAS" : "MYGOV";
};

const setLastProvider = (p: Provider) => {
  localStorage.setItem(LAST_PROVIDER_KEY, p);
  try {
    sessionStorage.setItem("loggedMethod", p);
  } catch {}
};

const detectProviderFromCallbackPath = (): Provider => {
  const path = (window.location.pathname || "").toLowerCase();
  return path.includes("eidas") ? "EIDAS" : "MYGOV";
};

// ----------------- UserManager cache -----------------
// NOTE: do NOT cache the one-off eIDAS callback UserManager that contains client_secret.
const managers: Partial<Record<Provider, UserManager>> = {};

// ----------------- Helpers: MyGov (Azure B2C) URL shaping -----------------

function buildB2CAuthorizeEndpoint(baseOrFull: string): string {
  if (/\/oauth2\/v2\.0\/authorize\/?$/i.test(baseOrFull)) return baseOrFull.replace(/\/+$/, "");
  return `${baseOrFull.replace(/\/+$/, "")}/oauth2/v2.0/authorize`;
}

function inferAuthorityForB2C(authorizeBaseOrFull: string): string {
  try {
    const u = new URL(authorizeBaseOrFull);
    const cleanedPath = u.pathname
      .replace(/\/oauth2\/v2\.0\/authorize\/?$/i, "")
      .replace(/\/+$/, "");
    const authority = `${u.origin}${cleanedPath}`;
    return /\/b2c_[^/]+$/i.test(authority) ? `${authority}/v2.0` : authority;
  } catch {
    return authorizeBaseOrFull;
  }
}

function inferAuthorityForEidas(fullAuthorizeEndpoint: string): string {
  try {
    const u = new URL(fullAuthorizeEndpoint);
    return u.origin;
  } catch {
    return fullAuthorizeEndpoint;
  }
}

// ----------------- eIDAS secret exchange (BFF-style) -----------------

async function exchangeEidasSecretForClientSecret(): Promise<string | null> {
  const secretToken = sessionStorage.getItem("eidas_secret_token");
  if (!secretToken) return null;

  try {
    const r = await fetch("/session/eidas-secret-exchange", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ secretToken }),
      cache: "no-store",
    });

    if (!r.ok) {
      const t = await r.text().catch(() => "");
      logger.warn(`[eIDAS] secret-exchange failed: ${r.status} ${t}`);
      return null;
    }

    const data = await r.json().catch(() => null);
    const clientSecret = data?.clientSecret;
    if (clientSecret) {
      sessionStorage.removeItem("eidas_secret_token");
      return clientSecret;
    }
    return null;
  } catch (e) {
    logger.warn("[eIDAS] secret-exchange error:", e);
    return null;
  }
}

// ----------------- initUserManager -----------------

export const initUserManager = async (
  provider: Provider = "MYGOV",
  clientSecret?: string
): Promise<UserManager> => {
  const useCache = !(provider === "EIDAS" && clientSecret);
  if (useCache && managers[provider]) return managers[provider]!;

  const cfg = await fetchAuthConfig();
  const isEidas = provider === "EIDAS";

  const authorizeBase = isEidas ? cfg.authorizeEndpointEidas! : cfg.authorizeEndpoint;
  const tokenEndpoint = isEidas ? cfg.tokenEndpointEidas! : cfg.tokenEndpoint;
  const clientId = isEidas ? cfg.clientIdEidas! : cfg.clientId;
  const redirectUri = isEidas ? cfg.redirectEndpointEidas! : cfg.redirectEndpoint;

  if (!isEidas) {
    const authorization_endpoint = buildB2CAuthorizeEndpoint(authorizeBase);
    const authority = inferAuthorityForB2C(authorization_endpoint);

    const um = new UserManager({
      authority,
      client_id: clientId,
      redirect_uri: redirectUri,
      response_type: "code",
      scope: cfg.scope ?? "openid",
      post_logout_redirect_uri: redirectUri,
      metadata: {
        authorization_endpoint,
        token_endpoint: tokenEndpoint,
      },
      userStore: new WebStorageStateStore({ store: window.localStorage }),
      automaticSilentRenew: false,
      monitorSession: false,
    });

    if (useCache) managers[provider] = um;
    return um;
  }

  const authorization_endpoint = authorizeBase;
  const authority = inferAuthorityForEidas(authorization_endpoint);

  const um = new UserManager({
    authority,
    client_id: clientId,
    redirect_uri: redirectUri,
    response_type: "code",
    scope: "openid profile",
    loadUserInfo: false,
    metadata: {
      authorization_endpoint,
      token_endpoint: tokenEndpoint,
    },
    ...(clientSecret
      ? {
          client_secret: clientSecret,
          token_endpoint_auth_method: cfg.tokenAuthMethodEidas ?? "client_secret_post",
          disablePKCE: true,
        }
      : {}),
    userStore: new WebStorageStateStore({ store: window.localStorage }),
    automaticSilentRenew: false,
    monitorSession: false,
  });

  if (useCache) managers[provider] = um;
  return um;
};

// ----------------- Public API -----------------

export const login = async (provider: Provider = "MYGOV") => {
  setLastProvider(provider);

  if (provider === "EIDAS") {
    await fetch("/session/status", {
      method: "GET",
      credentials: "include",
      cache: "no-store",
    });

    const r = await fetch("/session/eidas-secret-token", {
      method: "POST",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      cache: "no-store",
    });

    if (!r.ok) {
      const t = await r.text().catch(() => "");
      throw new Error(`Failed to get eIDAS secret token: ${r.status} ${t}`);
    }

    const { secretToken } = await r.json();
    if (!secretToken) throw new Error("No secretToken returned");
    sessionStorage.setItem("eidas_secret_token", secretToken);
  }

  const um = await initUserManager(provider);

  if (provider === "EIDAS") {
    await um.removeUser().catch(() => {});
  }

  return um.signinRedirect({
    state: `prov:${provider}`,
    ...(provider === "EIDAS"
      ? { extraQueryParams: { prompt: "login", max_age: "0" } }
      : {}),
  });
};

export const completeLogin = async (providerArg?: Provider): Promise<User | null> => {
  const provider = providerArg ?? detectProviderFromCallbackPath() ?? getLastProvider();

  try {
    const clientSecret =
      provider === "EIDAS" ? await exchangeEidasSecretForClientSecret() : undefined;

    const um = await initUserManager(provider, clientSecret || undefined);
    const user = await um.signinRedirectCallback();

    logger.info(`[OIDC] ${provider} callback completed`);

    await storeSessionOnServer(user.access_token ?? "", {
      idToken: user.id_token,
      loggedMethod: provider,
    });

    setLastProvider(provider);
    return user;
  } catch (e) {
    logger.error(`completeLogin(${provider}) failed`, e);
    return null;
  }
};

export const getToken = async (): Promise<string | null> => {
  try {
    const provider = getLastProvider();
    const um = await initUserManager(provider);
    const u = await um.getUser();
    if (!u || u.expired) return null;
    return u.access_token ?? null;
  } catch (e) {
    logger.error("[OIDC] getToken failed:", e);
    return null;
  }
};

export const logout = async (): Promise<void> => {
  try {
    const provider = getLastProvider();
    const um = await initUserManager(provider);

    await apiFetch(
      "/session/logout",
      { method: "POST", credentials: "include" },
      { skipLogoutOn401: true }
    ).catch(() => {});

    await um.removeUser();
    logger.info("Logout complete");
  } catch (e) {
    logger.error("Logout failed:", e);
  }
};

export const storeSessionOnServer = async (
  token: string,
  opts?: { idToken?: string; csrfToken?: string; loggedMethod?: Provider }
): Promise<
  | {
      email?: string;
      firstName?: string;
      lastName?: string;
      ppsn?: string;
      birthDate?: string;
    }
  | undefined
> => {
  const mask = (t?: string) =>
    t ? `${t.slice(0, 6)}…${t.slice(-6)} (len=${t.length})` : "(none)";

  logger.info("Preparing to send session login request", {
    accessToken: mask(token),
    idToken: mask(opts?.idToken),
    csrf: opts?.csrfToken ? "present" : "none",
    method: opts?.loggedMethod ?? "MYGOV",
  });

  const headers: Record<string, string> = { "Content-Type": "application/json" };
  if (opts?.csrfToken) headers["X-CSRF-Token"] = opts.csrfToken;

  const body = {
    access_token: token,
    id_token: opts?.idToken,
    loggedMethod: opts?.loggedMethod ?? "MYGOV",
  };

  const resp = await fetch("/session/login", {
    method: "POST",
    credentials: "include",
    headers,
    body: JSON.stringify(body),
  });

  if (resp.status === 401 || resp.status === 403) {
    logger.warn(`Session login unauthorized: ${resp.status}`);
    return undefined;
  }
  if (!resp.ok) {
    const text = await resp.text().catch(() => "");
    throw new Error(`Session login failed: ${resp.status} ${text}`);
  }

  await primeCsrf();
  const profile = await finalizeSessionOnClient();
  return profile;
};

// ----------------- React Context -----------------

export interface UserData {
  firstname: string;
  surname: string;
  ppsn: string;
  dob: string;
}

interface OidcAuthContextType {
  isAuthenticated: boolean;
  user: UserData | null;
  currentProvider: Provider;
  setCurrentProvider: (p: Provider) => void;
  getToken: () => Promise<string | null>;
  login: (p?: Provider) => Promise<void>;
  completeLogin: (p?: Provider) => Promise<User | null>;
  logout: () => Promise<void>;
}

const OidcAuthContext = createContext<OidcAuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [user, setUser] = useState<UserData | null>(null);
  const [currentProvider, setCurrentProviderState] = useState<Provider>(getLastProvider());

  const setCurrentProvider = (p: Provider) => {
    setCurrentProviderState(p);
    setLastProvider(p);
  };

  const hydrateFromOidcUser = useCallback(async () => {
    try {
      const provider = getLastProvider();
      const um = await initUserManager(provider);
      const u = await um.getUser();

      if (!u || u.expired) {
        setIsAuthenticated(false);
        setUser(null);
        return;
      }

      const tokenForClaims = u.id_token ?? u.access_token ?? null;
      if (!tokenForClaims) {
        setIsAuthenticated(false);
        setUser(null);
        return;
      }

      const decoded: any = jwtDecode(tokenForClaims);
      setUser({
        firstname: decoded.firstName || decoded.given_name || "",
        surname: decoded.lastName || decoded.family_name || "",
        ppsn: decoded.PublicServiceNumber || decoded.ppsn || decoded.sub || "",
        dob: decoded.BirthDate || decoded.birthdate || decoded.birthDate || "",
      });
      setIsAuthenticated(true);
    } catch (e) {
      logger.error("hydrateFromOidcUser failed:", e);
      setIsAuthenticated(false);
      setUser(null);
    }
  }, []);

  useEffect(() => {
    setLastProvider(currentProvider);
    void hydrateFromOidcUser();
  }, [currentProvider, hydrateFromOidcUser]);

  return (
    <OidcAuthContext.Provider
      value={{
        isAuthenticated,
        user,
        currentProvider,
        setCurrentProvider,
        getToken,
        login,
        completeLogin,
        logout,
      }}
    >
      {children}
    </OidcAuthContext.Provider>
  );
};

export const useOidcAuth = (): OidcAuthContextType => {
  const ctx = useContext(OidcAuthContext);
  if (!ctx) throw new Error("useOidcAuth must be used within AuthProvider");
  return ctx;
};

export { logout as myGovLogout };
